import { useAppSelector } from "store/index";
import { selectCurrentUserId, selectWorksheetIdByPage } from "store/selectors";
import {
  CourseCapability,
  OrgCapability,
  selectCourseCapabilities,
  selectCourseCapabilitiesByCourseId,
  selectCourseCapabilitiesByUserId,
  selectCourseUserIdsWithCourseCapability,
  selectOrgCapabilities,
  selectUserIdsWhoCanAuthorCourse,
  selectUserIdsWhoCanParticipateInCourse,
  selectUserPageCapabilities,
} from "store/selectors/permissions";
import { useListSelector } from "store/store-hooks";

/**
 * Hook to get all course-level capabilities for the current user in the current course and
 * all org-level capabilities for the current user in the current org.
 *
 * @returns An object containing all capabilities
 *
 * @example
 * ```tsx
 * const capabilities = useCapabilities();
 * // Returns: { canAuthorOrg: false, canAuthorCourse: true, ... }
 * ```
 */
export const useCapabilities = () => {
  const courseCapabilities = useAppSelector(selectCourseCapabilities);
  const orgCapabilities = useAppSelector(selectOrgCapabilities);
  return { ...courseCapabilities, ...orgCapabilities } as Record<
    CourseCapability | OrgCapability,
    boolean
  >;
};

/**
 * Hook to get all capabilities for a specific user in the current course.
 *
 * @param userId - The ID of the user to get capabilities for
 * @returns An object containing the user's capabilities
 *
 * @example
 * ```tsx
 * const userCapabilities = useCourseCapabilitiesByUserId('user123');
 * // Returns: { canAuthorCourse: false, canParticipateInCourse: true, ... }
 * ```
 */
export const useCourseCapabilitiesByUserId = (userId: string) => {
  // Note: currently, this only contains course capabilities. If needed, we can add org capabilities here
  // similarly to useCapabilities.
  return useAppSelector((state) => selectCourseCapabilitiesByUserId(state, userId));
};

/**
 * Hook to get a list of course user IDs that have a specific capability.
 *
 * @param capability - The capability to filter course users by
 * @returns An array of course user IDs that have the specified capability
 */
export const useCourseUserIdsWithCourseCapability = (capability: CourseCapability) => {
  return useListSelector((state) => selectCourseUserIdsWithCourseCapability(state, capability));
};

/**
 * Hook to get all course-level capabilities for the current user in a specific course.
 *
 * @param courseId - The ID of the course to get capabilities for
 * @returns An object containing the course's capabilities
 *
 * @example
 * ```tsx
 * const courseCapabilities = useCourseCapabilitiesByCourseId('course456');
 * // Returns: { canAuthorCourse: false, canParticipateInCourse: true, ... }
 * ```
 */
export const useCourseCapabilitiesByCourseId = (courseId: string) => {
  return useAppSelector((state) => selectCourseCapabilitiesByCourseId(state, courseId));
};

/**
 * Hook to get a list of user IDs for the users that can participate in the current course.
 *
 * @returns An array of user IDs
 */
export const useUserIdsWhoCanParticipateInCourse = () => {
  return useListSelector(selectUserIdsWhoCanParticipateInCourse);
};

/**
 * Hook to get a list of user IDs for the users that can author the current course.
 *
 * @returns An array of user IDs
 */
export const useUserIdsWhoCanAuthorCourse = () => {
  return useListSelector(selectUserIdsWhoCanAuthorCourse);
};

/**
 * Hook to get all page-level capabilities for the current user on a specific page.
 *
 * @param pageId - The ID of the page to get capabilities for
 * @returns An object containing the page's capabilities
 */
export const usePageCapabilitiesForPageId = (pageId: string) => {
  const worksheetId = useAppSelector((s) => selectWorksheetIdByPage(s, pageId));
  const currentUserId = useAppSelector(selectCurrentUserId);

  return useAppSelector((state) =>
    selectUserPageCapabilities(state, pageId, worksheetId, currentUserId!),
  );
};
